import * as React from 'react';
import { AlertTriangle, FileText, Info, Upload, X } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { StageProgressBar } from '@/components/ui/stage-progress';
import { DocumentProcessingStatus } from '@/components/documents/document-processing-status';
import { PartyChoice } from '@/components/documents/party-choice';
import { processingView } from '@/components/documents/processing-logic';
import { MAX_UPLOAD_SIZE_LABEL, uploadFileChunked } from '@/lib/api/documents';
import {
  useDocumentProcessingQuery,
  type DocumentProcessingItem,
} from '@/lib/api/document-processing';
import {
  type BaselineDocFileState,
  type BaselineDocsStepState,
  type NewEngagementWizardState,
} from '@/lib/wizard/new-engagement-state';
import { cn } from '@/lib/utils/cn';

import { hydrateBaselineFiles } from './baseline-files';

/**
 * Step 3 — Baseline documents pane.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals-wizard.js
 * → stepDocs()` (L147–185) + `wizard.css → .dropzone / .upload-row /
 * .upload-progress`. The prototype shows 5 demo "preset" chips —
 * per C20-131 those are demo seeds, replaced here with a real
 * file-input + drag-drop dropzone.
 *
 * Backend wire-up (T-0040 chunked upload + T-0044 list):
 *   0. "Whose document is this?" above the dropzone (E19 / T-0215): Vendor
 *      (the default) · Avero · Client, the same choice the upload modal
 *      asks, kept in the wizard state so it survives a step back. The
 *      answer goes on every initiate.
 *   1. User picks files via Browse or drop → for each file, call
 *      `uploadFileChunked(file, { projectId, sourceParty })`. The hook
 *      returns a `document_id` when the full chunked flow completes.
 *   2. While the upload is in flight, the row shows "Uploading N%": the
 *      share of the file's bytes the server has received, from the chunked
 *      upload helper's `onProgress` (T-0160).
 *   3. After completion, the row follows the document's pipeline stage from
 *      the documents list (`useDocumentProcessingQuery`, T-0159), polled
 *      every 3s until every document is ready or failed: "Reading 40% · 52
 *      of 128 pages", "Indexing 63% · 2,914 of 4,640 passages", "Ready ·
 *      128 pages" — real counts from the server. A failure names the stage
 *      ("Read 128 pages · search indexing failed"), gives the reason in
 *      plain words, and offers Retry when a retry can help.
 *
 * The step is optional — the wizard's Continue gate doesn't require
 * any docs. The info note below the dropzone mirrors the prototype's
 * "Baseline docs power the AI base-formation pass…" copy. With nothing
 * uploaded the step says so (T-0228, C2-42): the next step then has
 * nothing to read and starts no run.
 *
 * Accepted files are the API's general allowlist: PDF, Word, Excel and
 * plain text (TXT is read by the native parser as one page). A refused
 * file shows the API's reason and never counts as uploaded.
 */
export interface StepProps {
  /** Functional per-row updater — reads the latest files array. */
  updateBaselineFiles: (
    updater: (
      files: import('@/lib/wizard/new-engagement-state').BaselineDocsStepState['files'],
    ) => import('@/lib/wizard/new-engagement-state').BaselineDocsStepState['files'],
  ) => void;
  state: NewEngagementWizardState;
  /**
   * Patch helper supplied by the host so the step doesn't manipulate
   * wizard state directly. We pass full snapshots of `files` rather
   * than per-index mutations to keep the patch shape simple.
   */
  setBaselineDocs: (patch: Partial<BaselineDocsStepState>) => void;
}

/** Extensions and MIME types the file picker offers: the API's general allowlist. */
export const BASELINE_DOC_ACCEPT = [
  '.pdf',
  '.docx',
  '.xlsx',
  '.txt',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
].join(',');

export function StepBaselineDocs({ state, setBaselineDocs, updateBaselineFiles }: StepProps) {
  const { engagement, baselineDocs } = state;

  // Mounted only when we have a live engagement id (engagement minted at
  // the end of step 2). The hook handles its own polling cadence —
  // refetches every 3s while any doc is still moving, then stops.
  const docsList = useDocumentProcessingQuery(engagement?.engagement_id, {
    enabled: Boolean(engagement?.engagement_id),
  });
  const remoteById = React.useMemo(() => {
    const byId = new Map<string, DocumentProcessingItem>();
    for (const d of docsList.data?.documents ?? []) byId.set(d.id, d);
    return byId;
  }, [docsList.data]);

  // Hydrate the wizard rows from the backend list. We key by document_id
  // (set after the chunked-upload completes) and merge backend
  // `process_status` and the pipeline stage into the local row (step 4
  // waits on the stage).
  React.useEffect(() => {
    if (!docsList.data) return;
    updateBaselineFiles((rows) => hydrateBaselineFiles(rows, remoteById) ?? rows);
  }, [docsList.data, remoteById, updateBaselineFiles]);

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = React.useState(false);

  async function handleFiles(files: FileList | File[]) {
    const projectId = engagement?.project_id;
    if (!projectId) return;
    const arr = Array.from(files);
    // Append placeholder rows immediately so the user sees the upload
    // started.
    const newRows: BaselineDocFileState[] = arr.map((file) => ({
      client_id:
        typeof globalThis.crypto?.randomUUID === 'function'
          ? globalThis.crypto.randomUUID()
          : `cid-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: file.name,
      size_bytes: file.size,
      document_id: null,
      progress: 0,
      status: 'uploading',
      error: null,
    }));
    setBaselineDocs({ files: [...baselineDocs.files, ...newRows] });

    // Kick off chunked uploads in parallel. Each completes individually
    // and patches its own row by client_id.
    await Promise.all(
      arr.map(async (file, idx) => {
        const clientId = newRows[idx]!.client_id;
        try {
          const result = await uploadFileChunked(file, {
            projectId,
            sourceParty: baselineDocs.party,
            onProgress: ({ sentBytes, totalBytes }) => {
              const pct = totalBytes > 0 ? Math.floor((sentBytes / totalBytes) * 100) : 100;
              updateBaselineFiles((files) =>
                files.map((r) =>
                  r.client_id === clientId && r.status === 'uploading'
                    ? { ...r, progress: pct }
                    : r,
                ),
              );
            },
          });
          // After completion, patch the row with document_id + progress 100.
          // We read from the latest baselineDocs by callback to avoid races.
          updateBaselineFiles((files) =>
            files.map((r) =>
              r.client_id === clientId
                ? {
                    ...r,
                    document_id: result.document_id,
                    progress: 100,
                    status: 'pending' as const, // hydrates to extracted/indexed via poll
                  }
                : r,
            ),
          );
        } catch (err) {
          updateBaselineFiles((files) =>
            files.map((r) =>
              r.client_id === clientId
                ? {
                    ...r,
                    status: 'failed' as const,
                    error: err instanceof Error ? err.message : 'Upload failed.',
                  }
                : r,
            ),
          );
        }
      }),
    );
  }

  function removeRow(clientId: string) {
    setBaselineDocs({ files: baselineDocs.files.filter((f) => f.client_id !== clientId) });
  }

  const noEngagement = !engagement?.engagement_id;
  // A refused or failed upload never reached the server: it does not count.
  const nothingUploaded = !baselineDocs.files.some(
    (f) => f.document_id || f.status === 'uploading',
  );

  return (
    <div className="space-y-4">
      <PartyChoice
        value={baselineDocs.party}
        onChange={(party) => setBaselineDocs({ party })}
        disabled={noEngagement}
      />

      {/* Dropzone — real Browse button replaces the prototype's preset chips */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!noEngagement) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (noEngagement) return;
          if (e.dataTransfer?.files?.length) {
            void handleFiles(e.dataTransfer.files);
          }
        }}
        className={cn(
          'flex flex-col items-center gap-2 rounded-card border-[1.5px] border-dashed bg-paper-50 px-6 py-8 text-center',
          dragOver && !noEngagement
            ? 'border-accent-amber-600 text-ink-700'
            : 'border-ink-200 text-ink-500',
          noEngagement ? 'cursor-not-allowed opacity-60' : '',
        )}
      >
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink-100 bg-paper-0 text-ink-500">
          <Upload className="h-4.5 w-4.5" aria-hidden="true" />
        </span>
        <strong className="text-body-sm font-semibold text-ink-900">
          Drop files here or browse
        </strong>
        <span className="text-meta">
          PDF, DOCX, XLSX, TXT up to {MAX_UPLOAD_SIZE_LABEL} · chunked upload, resume-safe
        </span>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={noEngagement}
          className={cn(
            'mt-2 inline-flex h-8 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
            'hover:bg-paper-100 disabled:cursor-not-allowed disabled:opacity-50',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
          )}
        >
          Browse files
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          accept={BASELINE_DOC_ACCEPT}
          onChange={(e) => {
            if (e.target.files) {
              void handleFiles(e.target.files);
              e.target.value = '';
            }
          }}
        />
      </div>

      {noEngagement ? (
        <div
          role="alert"
          className="rounded-card border border-cite-broken/30 bg-paper-50 p-3 text-meta text-cite-broken"
        >
          Engagement not yet created. The wizard will create the engagement when you continue from
          step 2 (Modules). If you see this message after that point, the create-project call failed
          — go back and retry.
        </div>
      ) : null}

      {!noEngagement && nothingUploaded ? (
        <div
          role="status"
          className="flex items-start gap-2 rounded-card border border-posture-at-risk/40 bg-posture-at-risk-tint p-3 text-meta text-ink-700"
        >
          <AlertTriangle
            className="mt-0.5 h-3.5 w-3.5 flex-none text-posture-at-risk"
            aria-hidden="true"
          />
          <span>
            Nothing uploaded yet. The next step reads these documents; with none, there is nothing
            to extract and no run is started. You can add documents later from the Documents screen,
            and extraction starts on its own once one is indexed.
          </span>
        </div>
      ) : null}

      {/* Upload list */}
      {baselineDocs.files.length > 0 ? (
        <div className="space-y-1.5">
          {baselineDocs.files.map((f) => (
            <UploadRow
              key={f.client_id}
              file={f}
              engagementId={engagement?.engagement_id ?? ''}
              remote={f.document_id ? (remoteById.get(f.document_id) ?? null) : null}
              onRemove={() => removeRow(f.client_id)}
            />
          ))}
        </div>
      ) : null}

      <div className="flex items-start gap-2 text-meta text-ink-500">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden="true" />
        <span>
          Baseline docs power the AI base-formation pass. You can keep adding documents after
          engagement creation — this step is optional but recommended.
        </span>
      </div>

      {docsList.isFetching && !docsList.isLoading ? (
        <div className="flex items-center gap-2 text-meta text-ink-500">
          <Spinner /> Refreshing processing status…
        </div>
      ) : null}
    </div>
  );
}

function UploadRow({
  file,
  engagementId,
  remote,
  onRemove,
}: {
  file: BaselineDocFileState;
  engagementId: string;
  /** The document as the documents list reports it, once it is there. */
  remote: DocumentProcessingItem | null;
  onRemove: () => void;
}) {
  // The client-side upload failed: nothing reached the server.
  const uploadFailed = !file.document_id && file.status === 'failed';
  const view = remote ? processingView(remote) : null;

  return (
    <div className="grid grid-cols-[36px_1fr_110px_92px_30px] items-center gap-3 rounded-card border border-ink-100 bg-paper-0 px-3.5 py-2.5">
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-card bg-paper-100 text-ink-700">
        <FileText className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <div className="truncate text-body-sm font-medium text-ink-900">{file.name}</div>
        <div className="mt-1.5">
          {remote ? (
            <DocumentProcessingStatus engagementId={engagementId} document={remote} />
          ) : uploadFailed ? (
            <div className="text-meta font-medium text-posture-critical">Upload failed</div>
          ) : (
            <StageProgressBar
              percent={file.document_id ? null : file.progress}
              label={
                file.document_id
                  ? 'Waiting for the document to be read…'
                  : `Uploading ${file.progress}%`
              }
              ariaLabel={`Uploading ${file.name}`}
            />
          )}
        </div>
        {file.error ? <div className="mt-1 text-meta text-cite-broken">{file.error}</div> : null}
      </div>
      <span className="text-meta text-ink-500">{formatSize(file.size_bytes)}</span>
      <StatusChip
        label={view ? view.chip : uploadFailed ? 'Failed' : 'Uploading…'}
        tone={view ? view.tone : uploadFailed ? 'failed' : 'working'}
      />
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove file"
        className="inline-flex h-7 w-7 items-center justify-center rounded-card text-ink-500 hover:bg-paper-100 hover:text-ink-900"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

function StatusChip({ label, tone }: { label: string; tone: 'working' | 'done' | 'failed' }) {
  // The prototype's chip-healthy / chip-neutral affordances via the posture
  // tokens, plus the critical tint on failure.
  return (
    <span
      className={cn(
        'inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-2.5 text-[12px] font-medium leading-[18px]',
        tone === 'done' && 'bg-posture-healthy-tint text-posture-healthy',
        tone === 'failed' && 'bg-posture-critical-tint text-posture-critical',
        tone === 'working' && 'bg-ink-100 text-posture-unassessed',
      )}
    >
      {label}
    </span>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}
